

import { useState, useEffect, useRef } from "react"
import { FaImage, FaPaperPlane } from "react-icons/fa"
import AIResponse from "../AiResponse"
import { X, ZoomIn, ZoomOut, Download } from "lucide-react"

const ChatBox = ({ initialContext = "", screenshot = null }) => {
  const [message, setMessage] = useState("")
  const [responses, setResponses] = useState([])
  const [pastedImages, setPastedImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [imageTexts, setImageTexts] = useState([])
  const [transcribingIndex, setTranscribingIndex] = useState(-1)
  const [transcriptionErrors, setTranscriptionErrors] = useState([])
  const messagesEndRef = useRef(null)
  // Image preview state
  const [previewImage, setPreviewImage] = useState(null)
  const [zoomLevel, setZoomLevel] = useState(1)
  const API_URL = "https://math-image-transcriber-steveszren.replit.app/api/convert-diagram"
  const MAX_IMAGES = 5

  useEffect(() => {
    setResponses([
      {
        id: `welcome-${Date.now()}`,
        query: "",
        response: "Welcome! I'm your AIAT assistant. I can help you with past papers, answer questions about PDFs, and assist with any other inquiries you might have. Feel free to use the \\textbf{capture to Ask in Chat} to take a screenshot or ask me anything!",
        imageData: [],
        imageTexts: [],
      },
    ])
  }, [])

  // Initialize with screenshot if provided
  useEffect(() => {
    if (screenshot) {
      setPastedImages([screenshot])

      setImageTexts([])
      setTranscriptionErrors([])

      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement("canvas")
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0)

        canvas.toBlob((blob) => {
          if (blob) {
            transcribeImage(blob, 0)
          }
        }, "image/png")
      }
      img.crossOrigin = "anonymous"
      img.src = screenshot
    }
  }, [screenshot])

  useEffect(() => {
    if (initialContext && message === "") {
      setMessage(initialContext)
    }
  }, [initialContext])
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && previewImage) {
        closeImagePreview()
      }
    }

    window.addEventListener("keydown", handleEscKey)
    return () => window.removeEventListener("keydown", handleEscKey)
  }, [previewImage])
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTo({
        top: messagesEndRef.current.scrollHeight,
        behavior: "smooth" // or 'auto' for immediate scroll
      });
    }
  }, [responses]);

  const sendMessage = async () => {
    if (!message && imageTexts.length === 0) return
    setErrorMessage(null)

    let combinedMessage = message

    if (imageTexts.length > 0) {
      const validTexts = imageTexts.filter((text) => text)
      if (validTexts.length > 0) {
        combinedMessage += "\n\nTranscribed from images:\n"
        validTexts.forEach((text, index) => {
          combinedMessage += `\nImage ${index + 1}: ${text}\n`
        })
      }
    }

    setLoading(true)
    try {
      const lastTwoRounds = responses.slice(-2).flatMap((item) => [
        { role: "user", content: item.query },
        { role: "assistant", content: item.response },
      ])

      const fullConversation = [...lastTwoRounds, { role: "user", content: combinedMessage }]

      const requestBody = {
        conversation: fullConversation,
        screenshot: pastedImages.length > 0 ? true : false,
      }

      const response = await fetch("/api/chat/advanced", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })

      const data = await response.json()

      setResponses((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}`,
          query: message,
          response: data.response,
          imageData: pastedImages,
          imageTexts: imageTexts,
        },
      ])

      setMessage("")
      setPastedImages([])
      setImageTexts([])
      setTranscriptionErrors([])
    } catch (error) {
      console.error("API Error:", error)
      setErrorMessage("An unexpected error occurred. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const transcribeImage = async (file, index) => {
    if (!file) return

    setTranscribingIndex(index)

    setTranscriptionErrors((prev) => {
      const newErrors = [...prev]
      newErrors[index] = null
      return newErrors
    })

    try {
      const formData = new FormData()

      const imageFile = new File([file], "screenshot.png", { type: "image/png" })
      formData.append("image", imageFile)



      const response = await fetch(API_URL, {
        method: "POST",
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      if (data.success) {
        setImageTexts((prev) => {
          const newTexts = [...prev]
          newTexts[index] = data.result
          return newTexts
        })
      } else {
        throw new Error(data.error || "Failed to convert image")
      }
    } catch (err) {
      console.error("Transcription error:", err)
      setTranscriptionErrors((prev) => {
        const newErrors = [...prev]
        newErrors[index] = err.message || "Error transcribing image"
        return newErrors
      })

      setImageTexts((prev) => {
        const newTexts = [...prev]
        newTexts[index] = ""
        return newTexts
      })
    } finally {
      setTranscribingIndex(-1)
    }
  }

  const handlePaste = (event) => {
    const items = event.clipboardData.items
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        if (pastedImages.length >= MAX_IMAGES) {
          setErrorMessage(`Maximum of ${MAX_IMAGES} images allowed`)
          return
        }

        const file = item.getAsFile()
        const reader = new FileReader()

        reader.onload = (e) => {
          const newImageIndex = pastedImages.length
          setPastedImages((prev) => [...prev, e.target.result])

          transcribeImage(file, newImageIndex)
        }

        reader.readAsDataURL(file)
        break
      }
    }
  }

  const handleImageUpload = (event) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    if (pastedImages.length + files.length > MAX_IMAGES) {
      setErrorMessage(`Maximum of ${MAX_IMAGES} images allowed`)
      return
    }

    Array.from(files).forEach((file, fileIndex) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const newImageIndex = pastedImages.length + fileIndex
        setPastedImages((prev) => [...prev, e.target.result])

        transcribeImage(file, newImageIndex)
      }
      reader.readAsDataURL(file)
    })
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const removeImage = (index) => {
    setPastedImages((prev) => prev.filter((_, i) => i !== index))
    setImageTexts((prev) => prev.filter((_, i) => i !== index))
    setTranscriptionErrors((prev) => prev.filter((_, i) => i !== index))
  }
  // Image preview functions
  const openImagePreview = (imageUrl) => {
    setPreviewImage(imageUrl)
    setZoomLevel(1)
    document.body.style.overflow = "hidden"
  }

  const closeImagePreview = () => {
    setPreviewImage(null)
    setZoomLevel(1)
    document.body.style.overflow = "auto"
  }

  const zoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3))
  }

  const zoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))
  }

  const downloadImage = () => {
    if (!previewImage) return

    const link = document.createElement("a")
    link.href = previewImage
    link.download = `image-${Date.now()}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Chat Messages */}
      <div className="h-full overflow-y-auto p-4">
        {responses.map((item, index) => (
          <div
            ref={messagesEndRef}
            key={index} className="mb-4 animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
            {/* User Message */}
            <div className="flex justify-end mb-2">
              <div className="bg-gradient-to-r from-indigo-300 to-blue-400 text-white p-3 rounded-lg max-w-[80%] shadow-md">
                <p className="text-sm">{item.query}</p>

                {/* Show the images in the user message if they exist */}
                {item.imageData && item.imageData.length > 0 && (
                  <div className="mt-2">
                    {item.imageData.map((img, imgIndex) => (
                      <div key={imgIndex} className="relative">
                        <img
                          src={img || "/placeholder.svg"}
                          alt={`User uploaded ${imgIndex + 1}`}
                          className="rounded-lg max-w-full mt-2 border border-blue-400 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => openImagePreview(img)}                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* AI Response */}
            <div className="flex justify-start">
              <div className="bg-white p-3 rounded-lg max-w-[80%] shadow-md border border-gray-200 dark:border-gray-700">
              <AIResponse
                          content={item.response}
                          useTypewriter={true}
                        />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Image Preview */}
      {pastedImages.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-300 backdrop-blur-sm">
          <div className="flex flex-wrap gap-2">
            {pastedImages.map((img, index) => (
              <div key={index} className="relative group">
                <img
                  src={img || "/placeholder.svg"}
                  alt={`Uploaded ${index + 1}`}
                  className="h-16 w-16 object-cover rounded-lg border border-gray-300 dark:border-gray-600 transition-all group-hover:scale-105 cursor-pointer"
                  onClick={() => openImagePreview(img)}                />
                <button
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
                {transcribingIndex === index && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                {transcriptionErrors[index] && (
                  <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-xs p-1 text-center rounded-b-lg">
                    Error
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200 bg-gray-100 backdrop-blur-md">
        {errorMessage && (
          <div className="mb-2 text-sm text-red-500 dark:text-red-400 bg-red-100 dark:bg-red-900/30 p-2 rounded-lg">
            {errorMessage}
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="cursor-pointer flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full p-2 hover:from-blue-600 hover:to-blue-700 transition-all shadow-md">
            <FaImage className="text-white" />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
              multiple
              disabled={pastedImages.length >= MAX_IMAGES}
            />
          </label>

          <div className="flex-grow relative">
            <textarea
              className="w-full p-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl resize-none text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              rows="1"
              placeholder="Type your message..."
            />
          </div>

          <button
            onClick={sendMessage}
            disabled={loading || (!message.trim() && imageTexts.length === 0)}
            className={`p-3 rounded-full ${loading || (!message.trim() && imageTexts.length === 0)
              ? "bg-gray-300 text-gray-500"
              : "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700"
              } w-12 h-12 flex items-center justify-center shadow-md transition-all`}
          >
            {loading ? (
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <FaPaperPlane className="text-sm" />
            )}
          </button>
        </div>
      </div>
      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={closeImagePreview}
        >
          <div className="relative max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="absolute top-2 right-2 flex space-x-2 z-10">
              <button
                onClick={zoomIn}
                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                title="Zoom in"
              >
                <ZoomIn size={20} />
              </button>
              <button
                onClick={zoomOut}
                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                title="Zoom out"
              >
                <ZoomOut size={20} />
              </button>
              <button
                onClick={downloadImage}
                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                title="Download image"
              >
                <Download size={20} />
              </button>
              <button
                onClick={closeImagePreview}
                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                title="Close preview"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-auto max-h-[90vh] max-w-full flex items-center justify-center">
              <img
                src={previewImage || "/placeholder.svg"}
                alt="Preview"
                className="max-w-full object-contain transition-transform duration-200"
                style={{ transform: `scale(${zoomLevel})` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatBox

